import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Loader2, ArrowRight } from "lucide-react";
import { supabase } from "../../lib/supabase/client";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface ClaimBasedLoginProps {
  onBackToLogin: () => void;
}

export default function ClaimBasedLogin({ onBackToLogin }: ClaimBasedLoginProps) {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [step, setStep] = useState<"email" | "verification">("email");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Check if this email belongs to a claimed business
      const { data: businesses, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          company_name,
          user_metadata!user_metadata_user_id_fkey!inner(
            profile_claimed,
            password_set
          )
        `)
        .eq('email', email)
        .eq('user_metadata.profile_claimed', true)
        .single();

      if (error || !businesses) {
        toast.error("No claimed business found with this email address.");
        return;
      }

      // Check if password is already set
      const userMeta = Array.isArray(businesses.user_metadata) ? businesses.user_metadata[0] : businesses.user_metadata;
      if (userMeta?.password_set) {
        toast.info("This account already has a password. Please use the standard login.");
        onBackToLogin();
        return;
      }

      // Store business ID for later
      setBusinessId(businesses.id);

      // Send verification code
      const response = await fetch('http://localhost:3001/api/locksmiths/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: businesses.id,
          email: email,
          verificationMethod: 'email'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send verification code');
      }

      toast.success("Verification code sent to your email!");
      setStep("verification");
    } catch (error) {
      console.error("Email verification error:", error);
      toast.error("Failed to send verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Verify the code
      const response = await fetch('http://localhost:3001/api/locksmiths/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: businessId,
          code: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Verification failed');
      }

      // Create a temporary session
      const { data: sessionData, error: sessionError } = await supabase.rpc(
        'create_claimed_business_session',
        {
          p_business_id: businessId,
          p_email: email
        }
      );

      if (sessionError || !sessionData) {
        throw new Error('Failed to create session');
      }

      // Store session token and redirect to password setup
      sessionStorage.setItem('claimed_business_session', sessionData);
      sessionStorage.setItem('claimed_business_id', businessId?.toString() || '');
      sessionStorage.setItem('claimed_business_email', email);
      
      toast.success("Verification successful! Please set up your password.");
      navigate('/locksmith/setup-password');
    } catch (error) {
      console.error("Verification error:", error);
      toast.error(error instanceof Error ? error.message : "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-2">
          <div className="bg-primary/10 p-3 rounded-full">
            {step === "email" ? (
              <Mail className="h-6 w-6 text-primary" />
            ) : (
              <Lock className="h-6 w-6 text-primary" />
            )}
          </div>
        </div>
        <CardTitle className="text-2xl text-center">
          {step === "email" ? "Claimed Business Login" : "Enter Verification Code"}
        </CardTitle>
        <CardDescription className="text-center">
          {step === "email" 
            ? "Enter the email address associated with your claimed business"
            : `We've sent a verification code to ${email}`
          }
        </CardDescription>
      </CardHeader>

      <CardContent>
        {step === "email" ? (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Business Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="business@example.com"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  Send Verification Code
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm text-primary hover:underline"
              >
                Back to standard login
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerificationSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm font-medium">
                Verification Code
              </label>
              <Input
                id="code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                disabled={isLoading}
                maxLength={6}
                autoComplete="one-time-code"
              />
              <p className="text-xs text-muted-foreground">
                Check your email for the verification code
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </Button>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setVerificationCode("");
                }}
                className="text-sm text-primary hover:underline text-center"
              >
                Use a different email
              </button>
              <button
                type="button"
                onClick={handleEmailSubmit}
                className="text-sm text-muted-foreground hover:underline text-center"
                disabled={isLoading}
              >
                Resend code
              </button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}